<template>
  <UIModal
    v-model="isOpen"
    blured
    :back-button="!isSubmitDisabled"
    :close-button="!isSubmitDisabled"
    :close-on-click-outside="!isSubmitDisabled"
    @closed="emit('closed')"
    @back="emit('back')"
  >
    <!-- Header -->
    <template #header>
      <h2 class="halloween-expedition-modal__title">{{ $t('halloweenExpeditionStartModalTitle') }}</h2>
    </template>

    <!-- Content -->
    <div class="halloween-expedition-modal__content">
      <div class="halloween-expedition-modal__text">
        <p>{{ $t(`halloweenExpeditionLocationStart_${halloween.$state.runtimeSelectedLocation}`) }}.</p>
        <p>{{ $t('halloweenExpeditionCostText') }}</p>
      </div>

      <!-- Costs -->
      <div class="halloween-expedition-modal__costs">
        <div v-for="item in expeditionCosts" :key="item.address" class="halloween-expedition-modal__cost-item">
          <img
            :src="halloweenItemRegistry.resolveIcon(item)"
            alt="token"
            class="halloween-expedition-modal__cost-icon"
            @error="($event.target as HTMLImageElement).style.display = 'none'"
          />
          <span class="halloween-expedition-modal__cost-label">{{ $t(item.name) }}:</span>
          <span
            v-if="Number(halloween.getTokenBalance(item)) < item.quantity"
            class="halloween-expedition-modal__cost-amount error"
          >
            {{ halloween.getTokenBalance(item) }}/{{ new BigNumber(item.quantity).dp(1).toFormat() }}
          </span>
          <span v-else class="halloween-expedition-modal__cost-amount">
            {{ new BigNumber(item.quantity).dp(1).toFormat() }}
          </span>
        </div>
      </div>

      <!-- Slider -->
      <div class="halloween-expedition-modal__slider">
        <HalloweenSlider
          v-model="expeditionDuration"
          :disabled="isSubmitDisabled"
          :title="$t('HalloweenExpeditionDuration')"
          :min="sliderMin"
          :max="sliderMax"
          :step="1"
        />
      </div>
    </div>

    <!-- Footer -->
    <template #footer>
      <div class="halloween-expedition-modal__footer">
        <HalloweenGenericButton :disabled="props.isSubmitDisabled || !isEnoughBalance" @click="handleSubmit">
          {{ $t('halloweenExpeditionStartModalButton') }}
        </HalloweenGenericButton>
      </div>
    </template>
  </UIModal>
</template>

<script setup lang="ts">
import { BigNumber } from 'bignumber.js';
import { HalloweenItem, type ItemWithQuantity } from '~/types/halloween/items';
import halloweenItemRegistry from '~/utils/halloween/halloween-item-registry';

const halloween = useHalloweenStore();

const props = defineProps<{
  isSubmitDisabled?: boolean;
}>();

const isEnoughBalance = computed(() =>
  expeditionCosts.value
    .map((item) => {
      const balance = halloween.getTokenBalance(item);
      return Number(balance) >= item.quantity;
    })
    .every((value) => value)
);

const isOpen = defineModel<boolean>({ default: false });

const sliderConfig = computed(() => {
  if (!halloween.$state.runtimeSelectedLocation) return { min: 0, max: 0 };
  const config = halloween.$state.locationConfigurations[halloween.$state.runtimeSelectedLocation];

  if (!config) return { min: 0, max: 0 };
  return { min: config.minDuration / 3600, max: config.maxDuration / 3600 };
});

const expeditionDuration = ref(sliderConfig.value.min.toString());

const expeditionCosts = computed(() => {
  let gold = 0;
  let pearl = 0;
  const expeditionRelativeToken = parseInt(expeditionDuration.value.toString());
  halloween.$state.runtimeSelectedCharacters.forEach((character) => {
    gold += character.goldOrePrice;
    pearl += character.pearlPrice;
  });

  return [
    halloweenItemRegistry.withQuantity(HalloweenItem.GoldOre, gold),
    halloweenItemRegistry.withQuantity(HalloweenItem.Pearl, pearl),
    halloweenItemRegistry.withQuantity(
      halloween.$state.runtimeSelectedLocation === 'forest' ? HalloweenItem.Axe : HalloweenItem.Torch,
      expeditionRelativeToken
    )
  ];
});

const sliderMin = computed(() => sliderConfig.value.min);
const sliderMax = computed(() => sliderConfig.value.max);

watch(
  sliderConfig,
  (config) => {
    if (Number(expeditionDuration.value) < config.min) {
      expeditionDuration.value = config.min.toString();
    } else if (Number(expeditionDuration.value) > config.max) {
      expeditionDuration.value = config.max.toString();
    }
  },
  { immediate: true }
);

watch(isOpen, (newIsOpen) => {
  if (!newIsOpen) expeditionDuration.value = sliderConfig.value.min.toString();
});

const emit = defineEmits<{
  (e: 'submit', duration: number, expeditionCosts: ItemWithQuantity[]): void;
  (e: 'closed'): void;
  (e: 'back'): void;
}>();

function handleSubmit() {
  emit('submit', Number(expeditionDuration.value), expeditionCosts.value);
}
</script>

<style lang="scss" scoped>
.halloween-expedition-modal {
  &__title {
    font-family: 'PT Serif', serif;
    font-size: 32px;
    font-weight: bold;
    line-height: 30px;
    color: #ffffff;
    text-align: center;
    padding-left: 24px;
    padding-right: 24px;
    padding-bottom: 8px;

    @media (min-width: 640px) {
      padding-left: 48px;
      padding-right: 48px;
    }
  }

  &__content {
    font-family: 'PT Serif', serif;
    font-size: 18px;
    font-weight: 400;
    line-height: 110%;
    text-align: center;
    color: #dedede;
    padding-left: 24px;
    padding-right: 24px;

    max-width: 600px;
    margin: 0 auto;

    @media (min-width: 640px) {
      padding-left: 48px;
      padding-right: 48px;
    }

    @media screen and (max-width: 640px) {
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
  }

  &__text {
    margin-bottom: 20px;

    p:first-child {
      margin-bottom: 4px;
    }
  }

  &__costs {
    font-family: 'Eczar', serif;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 12px 24px;
    margin: 10px 0;
    font-size: 20px;
    line-height: 18px;
    color: #d1d5db;
  }

  &__cost-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__cost-icon {
    width: 40px;
    height: 40px;
  }

  &__cost-label {
    text-transform: capitalize;
    font-weight: 400;
  }

  &__cost-amount {
    font-weight: 700;

    &.error {
      color: #f6164c;
    }
  }

  &__slider {
    display: flex;
    justify-content: center;
  }

  &__footer {
    display: flex;
    justify-content: center;
  }
}
</style>
